'use strict';

/**
 * @fileoverview The ONE answer to "may a server open a connection to this address?" (SSRF-GUARD-ONE,
 * 2026-10-19). Both outbound guards ask it: the webhook dispatcher's (`@puffin-core/webhooks`
 * `urlSafety`) and the crawler's (`@puffin-core/site-crawl` `ssrfGuard`).
 *
 * The two guards had each asked ipaddr.js 1.9.1 for a `range()` label, and the labels were the defect.
 * It calls these 'unicast' (public):
 * - the deprecated IPv4-COMPATIBLE IPv6 block, so `[::127.0.0.1]` (the URL parser writes it
 *   `[::7f00:1]`) and `[::169.254.169.254]` passed the webhook guard;
 * - site-local `fec0::/10`, the discard prefix `100::/64`, ORCHIDv2 `2001:20::/28` and IPv4
 *   benchmarking `198.18.0.0/15`.
 * Each guard patched a different subset, so they disagreed on seven inputs.
 *
 * This is an ALLOW-list judged from DATA (the IANA special-purpose registries), not a label:
 * - IPv4 is outbound-safe when it lies outside every IPv4 special-purpose block and outside multicast.
 * - IPv6 is outbound-safe only inside global unicast (`2000::/3`) and outside every IPv6
 *   special-purpose block inside it.
 * - An IPv4-MAPPED address (`::ffff:0:0/96`) is the IPv4 address it carries, written for an IPv6
 *   socket, so it is judged as that IPv4.
 * - Every other IPv6 form that embeds an IPv4 address stays refused outright, whatever it embeds:
 *   - the deprecated IPv4-compatible block `::/96` and the NAT64 prefix `64:ff9b::/96`, both outside
 *     `2000::/3`;
 *   - 6to4 `2002::/16` and Teredo (inside `2001::/23`), both special-purpose blocks.
 *   No webhook or crawl target is served from a translation or tunnelling prefix. Judging the embedded
 *   address instead would ACCEPT some of them, which the two guards had refused, and a security gate
 *   is never loosened by generalising.
 *
 * ⚠️ ASSUMPTION: our APIs resolve over IPv4 or dual-stack, never DNS64. On an IPv6-only network with
 * DNS64, every IPv4-only target resolves to `64:ff9b::/96`, and this rule refuses it by design. Moving
 * an API onto such a network needs a ruling on NAT64 first (judge the embedded IPv4 there), not a
 * quiet widening here.
 *
 * Anything that is not a canonical address (a non-string, a short or zero-padded dotted quad, a
 * malformed IPv6 literal) is NOT safe. The guards hand over WHATWG-URL-normalised hostnames and DNS
 * answers, both canonical, so a non-canonical spelling here means something upstream changed.
 *
 * Pure and zero-dependency, like the rest of this package.
 *
 * @module @puffin-core/web-address/outboundAddress
 */

const IPV4_OCTET = /^(0|[1-9]\d{0,2})$/;
const IPV6_GROUP = /^[0-9a-f]{1,4}$/i;

/**
 * @param {string} text
 * @returns {number[]|null} a canonical dotted quad as two 16-bit groups, or null.
 */
function parseIpv4(text) {
  const octets = text.split('.');
  if (octets.length !== 4 || !octets.every((octet) => IPV4_OCTET.test(octet))) return null;
  const bytes = octets.map(Number);
  if (bytes.some((byte) => byte > 255)) return null;
  return [(bytes[0] << 8) | bytes[1], (bytes[2] << 8) | bytes[3]];
}

/**
 * @param {string} text - colon-separated groups; the last may be a dotted quad.
 * @returns {number[]|null} the groups as 16-bit numbers, or null when any is malformed.
 */
function parseGroups(text) {
  if (text === '') return [];
  const parts = text.split(':');
  const groups = [];
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (i === parts.length - 1 && part.includes('.')) {
      const tail = parseIpv4(part);
      if (tail === null) return null;
      groups.push(...tail);
    } else if (IPV6_GROUP.test(part)) {
      groups.push(parseInt(part, 16));
    } else {
      return null;
    }
  }
  return groups;
}

/**
 * @param {string} text - an IPv6 literal without brackets or zone.
 * @returns {number[]|null} the eight 16-bit groups, or null.
 */
function parseIpv6(text) {
  const halves = text.split('::');
  if (halves.length > 2) return null;
  const head = parseGroups(halves[0]);
  if (halves.length === 1) return head !== null && head.length === 8 ? head : null;
  const tail = parseGroups(halves[1]);
  if (head === null || tail === null || head.length + tail.length > 7) return null;
  return [...head, ...new Array(8 - head.length - tail.length).fill(0), ...tail];
}

/**
 * @param {string} cidr - `address/bits`, written by this module's own tables.
 * @param {function(string): (number[]|null)} parse
 * @returns {{groups: number[], bits: number}}
 */
function block(cidr, parse) {
  const [address, bits] = cidr.split('/');
  return Object.freeze({ groups: Object.freeze(parse(address)), bits: Number(bits) });
}

/**
 * The IANA IPv4 Special-Purpose Address Registry (RFC 6890 and its updates, read 2026-10-19), plus
 * multicast `224.0.0.0/4`, which has a registry of its own. Every entry is refused, including the few
 * IANA marks globally reachable (AS112, AMT, the deprecated 6to4 relay anycast): an outbound
 * webhook or crawl never targets an infrastructure anycast block.
 */
const IPV4_SPECIAL_PURPOSE = Object.freeze(
  [
    '0.0.0.0/8', // "this network"
    '10.0.0.0/8', // private
    '100.64.0.0/10', // shared address space (CGNAT)
    '127.0.0.0/8', // loopback
    '169.254.0.0/16', // link local, incl. the cloud metadata address
    '172.16.0.0/12', // private
    '192.0.0.0/24', // IETF protocol assignments
    '192.0.2.0/24', // documentation (TEST-NET-1)
    '192.31.196.0/24', // AS112-v4
    '192.52.193.0/24', // AMT
    '192.88.99.0/24', // deprecated 6to4 relay anycast
    '192.168.0.0/16', // private
    '192.175.48.0/24', // direct delegation AS112 service
    '198.18.0.0/15', // benchmarking
    '198.51.100.0/24', // documentation (TEST-NET-2)
    '203.0.113.0/24', // documentation (TEST-NET-3)
    '224.0.0.0/4', // multicast
    '240.0.0.0/4', // reserved, incl. limited broadcast 255.255.255.255
  ].map((cidr) => block(cidr, parseIpv4)),
);

/** IPv6 global unicast: every outbound-safe IPv6 address lies inside it. */
const IPV6_GLOBAL_UNICAST = block('2000::/3', parseIpv6);

/**
 * The IANA IPv6 Special-Purpose Address Registry entries that lie INSIDE `2000::/3` (read 2026-10-19).
 * Everything outside `2000::/3` is already refused: loopback, unspecified, `64:ff9b::/96`,
 * `64:ff9b:1::/48`, `100::/64`, `5f00::/16`, unique-local, link-local, site-local and multicast.
 * Listing those here would only add entries no address could reach.
 */
const IPV6_SPECIAL_PURPOSE = Object.freeze(
  [
    '2001::/23', // IETF protocol assignments: Teredo, benchmarking, AMT, AS112, ORCHIDv2, DRIP
    '2001:db8::/32', // documentation
    '2002::/16', // 6to4
    '2620:4f:8000::/48', // direct delegation AS112 service
    '3fff::/20', // documentation (RFC 9637)
  ].map((cidr) => block(cidr, parseIpv6)),
);

/** IPv4-mapped IPv6: its last 32 bits ARE an IPv4 address, judged as that IPv4. */
const IPV4_MAPPED = block('::ffff:0:0/96', parseIpv6);

/**
 * @param {number[]} groups
 * @param {{groups: number[], bits: number}} prefix
 * @returns {boolean} whether the first `prefix.bits` bits of `groups` equal the prefix's.
 */
function inBlock(groups, prefix) {
  for (let i = 0, bits = prefix.bits; bits > 0; i += 1, bits -= 16) {
    const shift = 16 - Math.min(16, bits);
    if (groups[i] >> shift !== prefix.groups[i] >> shift) return false;
  }
  return true;
}

/** @param {number[]} groups - an IPv4 address as two 16-bit groups. @returns {boolean} */
function isSafeIpv4(groups) {
  return !IPV4_SPECIAL_PURPOSE.some((prefix) => inBlock(groups, prefix));
}

/** @param {number[]} groups - an IPv6 address as eight 16-bit groups. @returns {boolean} */
function isSafeIpv6(groups) {
  if (inBlock(groups, IPV4_MAPPED)) {
    return isSafeIpv4(groups.slice(6));
  }
  return (
    inBlock(groups, IPV6_GLOBAL_UNICAST) &&
    !IPV6_SPECIAL_PURPOSE.some((prefix) => inBlock(groups, prefix))
  );
}

/**
 * @param {*} address
 * @returns {{family: 4|6, groups: number[]}|null} a canonical IP literal (a dotted quad, or IPv6 with or
 *   without brackets and a zone id), parsed; null for anything else, a hostname included.
 */
function parseAddress(address) {
  if (typeof address !== 'string') return null;
  const bare = address.replace(/^\[(.*)\]$/, '$1').split('%')[0];
  if (bare.includes(':')) {
    const groups = parseIpv6(bare);
    return groups === null ? null : { family: 6, groups };
  }
  const groups = parseIpv4(bare);
  return groups === null ? null : { family: 4, groups };
}

/**
 * @param {*} value - a URL hostname or a DNS answer.
 * @returns {4|6|null} the family of a canonical IP literal; null for a name or anything malformed.
 */
function ipAddressFamily(value) {
  const parsed = parseAddress(value);
  return parsed === null ? null : parsed.family;
}

/**
 * @param {*} address - an IP literal: a dotted quad, or IPv6 with or without brackets and a zone id.
 * @returns {boolean} whether a server may open an outbound connection to it (see the module doc).
 */
function isOutboundSafeAddress(address) {
  const parsed = parseAddress(address);
  if (parsed === null) return false;
  return parsed.family === 6 ? isSafeIpv6(parsed.groups) : isSafeIpv4(parsed.groups);
}

/**
 * Names that never reach a public server: `localhost` and the `.localhost` (RFC 6761), `.local`
 * (mDNS, RFC 6762) and `.internal` (ICANN's private-use TLD) zones. They were the webhook guard's own
 * list, and the crawler's guard had none, so it handed such a name to DNS.
 */
const NON_PUBLIC_NAME_SUFFIXES = Object.freeze(['.localhost', '.local', '.internal']);

/**
 * @param {string} name - a hostname as `outboundHostname` answers it (no trailing dot).
 * @returns {boolean} whether it names a host that is never a public server.
 */
function isNonPublicHostname(name) {
  return name === 'localhost' || NON_PUBLIC_NAME_SUFFIXES.some((suffix) => name.endsWith(suffix));
}

/**
 * The hostname an outbound guard judges: ONE trailing dot stripped (`localhost.` is `localhost`; the
 * fully-qualified spelling must not slip past a name check), or null when any label is empty
 * (`a..b`, `.`, `x..`).
 *
 * @param {*} hostname - a URL's `hostname` (an IPv6 literal keeps its brackets).
 * @returns {string|null}
 */
function outboundHostname(hostname) {
  if (typeof hostname !== 'string') return null;
  const name = hostname.endsWith('.') ? hostname.slice(0, -1) : hostname;
  return name.split('.').includes('') ? null : name;
}

module.exports = { isOutboundSafeAddress, ipAddressFamily, isNonPublicHostname, outboundHostname };
